import { clearToken, getUser } from "../lib/auth";
import { ensureAppTimezone, fmtDateTime } from "../lib/datetime";
import {
  getLLMSettings,
  primaryRole,
  saveLLMSettings,
  type LLMSettings,
} from "../lib/llm";
import { navigate } from "../lib/router";
import { APP_VERSION } from "../version";
import { getTheme, toggleTheme } from "../lib/theme";
import { openSearchPanel } from "./search_panel";

const NAV_ITEMS: Array<{ label: string; path: string }> = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Folder View", path: "/folders" },
  { label: "Graph View", path: "/graph" },
  { label: "My Signals", path: "/my-signals" },
  { label: "Pulse", path: "/pulse" },
  { label: "Threads", path: "/threads" },
  { label: "Stories", path: "/stories" },
  { label: "Judgments", path: "/judgments" },
  { label: "Signal Management", path: "/signals/manage" },
];

function initials(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

/**
 * The analysis-model picker.
 *
 * Lives in the header because the selection is app-wide: it drives scheduled
 * collection, manual runs, and the daily brief, so it belongs somewhere every
 * page can see rather than buried in one tab of Signal Management. The chip
 * shows the pass-2 model, since that is the one whose output ends up stored on
 * the signal.
 *
 * Read-only for non-admins — the setting is global, so one analyst switching it
 * would change what everyone else's corpus gets analysed with.
 *
 * The Telegram bot has its own row here now. It used to be excluded — the agent
 * could only speak Anthropic tool use — which is why this was headed "Analysis
 * models"; backend/llm_agent.py removed that limit and the heading with it.
 * What the bot still does not govern is signal *analysis*: an add_signal from
 * Telegram runs on the pass-2 model like every other analysis, so the two can be
 * on different providers and the panel warns when that combination is broken.
 */
function renderModelPicker(): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "model-picker";

  const chip = document.createElement("button");
  chip.className = "model-chip";
  chip.type = "button";
  chip.title = "Models";
  chip.setAttribute("aria-label", "Models");
  // A glyph rather than the model label: the label was the widest thing in the
  // header's right-hand cluster and pushed it off a 375px screen. The model name
  // moves to the tooltip and the accessible name, and the colour still carries
  // the one state you must not miss (see .model-chip-warn).
  chip.textContent = "✨";
  wrap.appendChild(chip);

  let menu: HTMLDivElement | null = null;

  const closeMenu = () => {
    if (!menu) return;
    menu.remove();
    menu = null;
    document.removeEventListener("click", outsideClick, true);
  };
  const outsideClick = (e: MouseEvent) => {
    if (!menu) return;
    if (!menu.contains(e.target as Node) && !chip.contains(e.target as Node)) closeMenu();
  };

  const paintChip = (settings: LLMSettings) => {
    const role = primaryRole(settings);
    if (!role) return;
    chip.classList.toggle("model-chip-warn", !role.configured);
    chip.title = role.configured
      ? `Models — pass 2 runs on ${role.modelLabel}`
      : `${role.modelLabel} has no API key configured — signals cannot be analysed`;
    // The glyph is fixed, so the tooltip is the only place the model name appears.
    // Mirror it into the accessible name or a screen reader gets "Models" and
    // never learns which model is active.
    chip.setAttribute("aria-label", chip.title);
  };

  // Best-effort: a failed fetch leaves the chip on its generic tooltip rather
  // than breaking the header on every page.
  void getLLMSettings()
    .then(paintChip)
    .catch(() => {
      chip.title = "Models — could not load the current selection";
      chip.setAttribute("aria-label", chip.title);
    });

  chip.addEventListener("click", async () => {
    if (menu) {
      closeMenu();
      return;
    }
    menu = document.createElement("div");
    menu.className = "model-menu";
    menu.innerHTML = `<p class="model-menu-loading muted">Loading…</p>`;
    document.body.appendChild(menu);
    positionMenu(menu, chip);
    setTimeout(() => document.addEventListener("click", outsideClick, true), 0);

    let settings: LLMSettings;
    try {
      // The panel shows a timestamp, so the zone has to be here before it paints.
      void ensureAppTimezone();
      settings = await getLLMSettings(true);
    } catch {
      if (menu) menu.innerHTML = `<p class="model-menu-loading muted">Could not load models.</p>`;
      return;
    }
    if (!menu) return;
    paintChip(settings);
    buildMenu(menu, settings, paintChip, closeMenu);
    positionMenu(menu, chip);
  });

  return wrap;
}

function positionMenu(menu: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  menu.style.top = `${rect.bottom + 8}px`;
  // Clamp to the viewport so the panel doesn't hang off a narrow screen.
  const width = menu.offsetWidth || 320;
  const left = Math.min(rect.left, Math.max(8, window.innerWidth - width - 8));
  menu.style.left = `${Math.max(8, left)}px`;
}

function buildMenu(
  menu: HTMLElement,
  settings: LLMSettings,
  onSaved: (s: LLMSettings) => void,
  close: () => void
): void {
  menu.innerHTML = "";

  // Titled "Analysis models" while it governed only collection and briefs —
  // the Telegram bot was pinned to Claude and deliberately out of scope. It has
  // its own role now, so the narrower label would be a lie.
  const title = document.createElement("h4");
  title.textContent = "Models";
  menu.appendChild(title);

  const sub = document.createElement("p");
  sub.className = "muted model-menu-sub";
  sub.textContent = settings.canEdit
    ? "Applies to signal collection, daily briefs and the Telegram bot, for everyone."
    : "Applies to signal collection, daily briefs and the Telegram bot. Only admins can change this.";
  menu.appendChild(sub);

  const selects = new Map<string, HTMLSelectElement>();

  for (const role of settings.roles) {
    const row = document.createElement("div");
    row.className = "model-menu-row";

    const label = document.createElement("label");
    label.className = "model-menu-label";
    label.textContent = role.label;
    row.appendChild(label);

    if (settings.canEdit) {
      const select = document.createElement("select");
      select.className = "model-menu-select";
      for (const model of settings.catalogue.filter((m) => m.roles.includes(role.role))) {
        const opt = document.createElement("option");
        opt.value = model.id;
        opt.textContent = model.free ? `${model.label} — free` : model.label;
        opt.title = model.note;
        select.appendChild(opt);
      }
      if (!role.known) {
        // A model the catalogue does not list — typed by hand, or dropped from
        // the catalogue since it was chosen. Show it rather than silently
        // resetting the analyst's choice; saving sends "" for this role, which
        // the API reads as "leave it alone".
        //
        // The label used to read "(from config.yaml)", which was a guess: being
        // absent from the catalogue says nothing about where a value came from.
        // The real answer is `role.sourceLabel`, rendered below for every row.
        const opt = document.createElement("option");
        opt.value = "";
        opt.textContent = `${role.model} (not in the catalogue)`;
        select.appendChild(opt);
      }
      select.value = role.known ? role.model : "";
      label.htmlFor = `model-select-${role.role}`;
      select.id = `model-select-${role.role}`;
      selects.set(role.role, select);
      row.appendChild(select);
    } else {
      const value = document.createElement("span");
      value.className = "model-menu-value";
      value.textContent = role.modelLabel;
      row.appendChild(value);
    }

    // Where this row's model came from. Every row, not just the odd one: the
    // failure worth preventing is an operator editing a source that is being
    // overridden, and they can only avoid that if the winner is always named.
    const origin = document.createElement("p");
    origin.className = "model-menu-origin";
    // Rendered verbatim: the backend already words the fallback case as
    // "follows Pass 2 — signal analysis", and phrasing it in both places is how
    // the two come to disagree.
    origin.textContent = role.sourceLabel;
    row.appendChild(origin);

    // The state that cost a week of daily briefs: config.yaml says one thing,
    // the picker says another, the picker wins, and nothing anywhere said so.
    if (role.shadowedBy === "config_yaml" && role.shadowedModel) {
      const shadow = document.createElement("p");
      shadow.className = "model-menu-warn";
      shadow.textContent =
        `config.yaml names ${role.shadowedModel} for this role, but the setting ` +
        `here wins — editing config.yaml will not change it.`;
      row.appendChild(shadow);
    }

    if (!role.configured) {
      const warn = document.createElement("p");
      warn.className = "model-menu-warn";
      // Each role fails differently, and "analysis is skipped" said to someone
      // whose daily brief has quietly stopped arriving is a wrong answer, not a
      // vague one.
      const MISSING_KEY: Record<string, string> = {
        agent: "No API key configured for this provider — the bot cannot reply.",
        brief: "No API key configured for this provider — briefs fall back to this role and cannot be generated.",
        brief_daily: "No API key configured for this provider — the daily brief cannot be generated.",
        brief_weekly: "No API key configured for this provider — the weekly brief cannot be generated.",
        watch_area_review: "No API key configured for this provider — the watch-area review cannot run.",
      };
      warn.textContent =
        MISSING_KEY[role.role] ??
        "No API key configured for this provider — analysis is skipped.";
      row.appendChild(warn);
    }

    menu.appendChild(row);
  }

  // A combination that is broken as a whole while every role looks fine on its
  // own: the bot answering happily on a keyed provider while every add_signal
  // fails against an unkeyed pass-2 model. Rendered once, below the rows,
  // because it is about the relationship between them.
  if (settings.warning) {
    const warn = document.createElement("p");
    warn.className = "model-menu-warn";
    warn.textContent = settings.warning;
    menu.appendChild(warn);
  }

  const status = document.createElement("p");
  status.className = "model-menu-status muted";
  if (settings.updatedAt) {
    status.textContent = `Last changed ${fmtDateTime(settings.updatedAt)}${
      settings.updatedBy ? ` by ${settings.updatedBy}` : ""
    }`;
  }
  menu.appendChild(status);

  if (!settings.canEdit) return;

  const actions = document.createElement("div");
  actions.className = "model-menu-actions";
  const save = document.createElement("button");
  save.className = "primary";
  save.type = "button";
  save.textContent = "Save";
  actions.appendChild(save);
  menu.appendChild(actions);

  save.addEventListener("click", async () => {
    save.disabled = true;
    save.textContent = "Saving…";
    status.classList.remove("model-menu-error");
    try {
      // Spelled out rather than built from `selects`, matching the payload
      // type: a role missing here is dropped silently on save, so the two
      // lists have to be kept in step by hand.
      const updated = await saveLLMSettings({
        relevanceCheck: selects.get("relevance_check")?.value ?? "",
        fullAnalysis: selects.get("full_analysis")?.value ?? "",
        agent: selects.get("agent")?.value ?? "",
        brief: selects.get("brief")?.value ?? "",
        briefDaily: selects.get("brief_daily")?.value ?? "",
        briefWeekly: selects.get("brief_weekly")?.value ?? "",
        watchAreaReview: selects.get("watch_area_review")?.value ?? "",
        storySummary: selects.get("story_summary")?.value ?? "",
        weeklyMap: selects.get("weekly_map")?.value ?? "",
        weeklySynthesis: selects.get("weekly_synthesis")?.value ?? "",
        backfill: selects.get("backfill")?.value ?? "",
      });
      onSaved(updated);
      close();
    } catch (err) {
      status.classList.add("model-menu-error");
      status.textContent = err instanceof Error ? err.message : "Could not save.";
      save.disabled = false;
      save.textContent = "Save";
    }
  });
}

export function renderHeader(): HTMLElement {
  const user = getUser();
  const header = document.createElement("header");
  header.className = "app-header";

  // Brand
  const brand = document.createElement("div");
  brand.className = "brand";
  brand.innerHTML = `<img src="/logo.png" class="brand-logo" alt="Kassandra" /><span>Kassandra</span>`;
  brand.addEventListener("click", () => navigate("/dashboard"));
  header.appendChild(brand);

  // Nav
  const nav = document.createElement("nav");
  for (const item of NAV_ITEMS) {
    const btn = document.createElement("button");
    btn.textContent = item.label;
    if (location.pathname.startsWith(item.path)) btn.classList.add("active");
    btn.addEventListener("click", () => {
      closeNav();
      navigate(item.path);
    });
    nav.appendChild(btn);
  }
  header.appendChild(nav);

  // Right controls
  const right = document.createElement("div");
  right.className = "right";

  // Version — compiled in at build time from the VERSION file plus the commit
  // count after it (scripts/bump_version.py). First in the cluster so it reads
  // as a label on the app rather than as another control, and so it sits left
  // of the hamburger where the menu opens from.
  const version = document.createElement("span");
  version.className = "app-version";
  version.textContent = APP_VERSION;
  version.title = `Kassandra ${APP_VERSION}`;
  right.appendChild(version);

  // Hamburger — hidden on desktop, shown on mobile via CSS
  const hamburger = document.createElement("button");
  hamburger.className = "header-hamburger icon";
  hamburger.title = "Menu";
  hamburger.setAttribute("aria-label", "Toggle navigation");
  hamburger.innerHTML = "☰";
  hamburger.addEventListener("click", () => {
    if (header.classList.contains("nav-open")) closeNav();
    else openNav();
  });
  right.appendChild(hamburger);

  /* The drawer had no way out but the hamburger itself or picking a
     destination: tapping the page behind it did nothing, which on a phone is
     the first thing anybody tries. The model picker and the account menu both
     already install an outside-click listener while open; this is the same
     pattern, and the listeners are added on open and removed on close rather
     than left on `document`, because the header is rebuilt on every navigation
     and permanent listeners would accumulate one per page visited.

     Declared as hoisted functions so the nav buttons above — which are built
     before the hamburger exists — can call closeNav(). */
  function closeNav(): void {
    header.classList.remove("nav-open");
    document.removeEventListener("click", navOutsideClick, true);
    document.removeEventListener("keydown", navEscape);
  }
  function openNav(): void {
    header.classList.add("nav-open");
    // Deferred, or the click that opened the drawer closes it again.
    setTimeout(() => {
      document.addEventListener("click", navOutsideClick, true);
      document.addEventListener("keydown", navEscape);
    }, 0);
  }
  function navOutsideClick(e: MouseEvent): void {
    const target = e.target as Node;
    if (!nav.contains(target) && !hamburger.contains(target)) closeNav();
  }
  function navEscape(e: KeyboardEvent): void {
    if (e.key === "Escape") closeNav();
  }

  right.appendChild(renderModelPicker());

  const searchBtn = document.createElement("button");
  searchBtn.className = "icon";
  searchBtn.title = "Search";
  searchBtn.setAttribute("aria-label", "Search");
  searchBtn.textContent = "⌕";
  // The search panel's scrim sits at z-index 800 and the drawer at 700, so an
  // open drawer would be left underneath it, visible and unreachable.
  searchBtn.addEventListener("click", () => {
    closeNav();
    openSearchPanel();
  });
  right.appendChild(searchBtn);

  const themeBtn = document.createElement("button");
  themeBtn.className = "icon";
  themeBtn.title = "Toggle theme";
  themeBtn.setAttribute("aria-label", "Toggle theme");
  themeBtn.textContent = getTheme() === "dark" ? "☀" : "☾";
  themeBtn.addEventListener("click", () => {
    const next = toggleTheme();
    themeBtn.textContent = next === "dark" ? "☀" : "☾";
  });
  right.appendChild(themeBtn);

  const feedbackBtn = document.createElement("button");
  // "icon" gives it the same 36x36 box as the search and theme buttons; the word
  // "Feedback" was the second-widest thing in this cluster.
  feedbackBtn.className = "icon feedback-btn";
  feedbackBtn.textContent = "💬";
  feedbackBtn.title = "Give feedback";
  feedbackBtn.setAttribute("aria-label", "Give feedback");
  feedbackBtn.addEventListener("click", () => {
    window.open("https://forms.gle/7srdTgNPUFckxXDw8", "_blank", "noopener,noreferrer");
  });
  right.appendChild(feedbackBtn);

  // User avatar + dropdown
  const avatarWrap = document.createElement("div");
  avatarWrap.style.position = "relative";
  const avatar = document.createElement("button");
  avatar.className = "user-avatar";
  avatar.title = user?.username ?? "Account";
  avatar.textContent = user ? initials(user.username) : "?";
  avatarWrap.appendChild(avatar);

  let menu: HTMLDivElement | null = null;
  const closeMenu = () => {
    if (menu) {
      menu.remove();
      menu = null;
      document.removeEventListener("click", outsideClick, true);
    }
  };
  const outsideClick = (e: MouseEvent) => {
    if (!menu) return;
    if (!menu.contains(e.target as Node) && !avatar.contains(e.target as Node)) closeMenu();
  };

  avatar.addEventListener("click", () => {
    if (menu) {
      closeMenu();
      return;
    }
    menu = document.createElement("div");
    menu.className = "user-menu";

    if (user?.role === "admin") {
      const adminBtn = document.createElement("button");
      adminBtn.textContent = "Admin Panel";
      adminBtn.addEventListener("click", () => {
        closeMenu();
        navigate("/admin");
      });
      menu.appendChild(adminBtn);
      const sep = document.createElement("div");
      sep.className = "separator";
      menu.appendChild(sep);
    }

    const accountBtn = document.createElement("button");
    accountBtn.textContent = "My Account";
    accountBtn.addEventListener("click", () => {
      closeMenu();
      navigate("/account");
    });
    menu.appendChild(accountBtn);

    const signOutBtn = document.createElement("button");
    signOutBtn.textContent = "Sign Out";
    signOutBtn.addEventListener("click", () => {
      closeMenu();
      clearToken();
      navigate("/login");
    });
    menu.appendChild(signOutBtn);

    // Appended to the avatar's wrapper, which is already `position: relative`
    // for exactly this purpose, rather than to <body>. It was going to
    // document.body, where `position: absolute` resolves against the initial
    // containing block — so `top: 56px` meant 56px from the top of the
    // *document*, and the menu rendered off-screen the moment the page was
    // scrolled. Measured at 544px above the viewport after a 600px scroll.
    avatarWrap.appendChild(menu);
    setTimeout(() => document.addEventListener("click", outsideClick, true), 0);
  });

  right.appendChild(avatarWrap);
  header.appendChild(right);

  return header;
}
